import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { PAGE_SIZE } from '@/Settings'
import { Page, PageHeader, Pagination, Table, TableCell, TableRow, TooltipAlertCircle, usePagination } from '@/ui'
import { webhooksQuery } from '../api'
import { WebhookRow, WebhookRowSkeleton } from '../components'
import { Button, useModal } from '@dashboard/ui'
import { useCurrentUser } from '@/api/useCurrentUser'
import { CreateWebhookModal } from '@/layouts/webhooks/widgets/CreateWebhookModal'
import { useEffect } from 'react'
import { GAME_WEBHOOKS_PATH, useGenerateCompanyPath } from '@/libs'
import { useNavigate } from 'react-router-dom'
import { WebhookDiscoverCallout, WebhookTableZeroState } from '../widgets'

export const WebhookTable = (props: { isCreating?: boolean }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { page, query, onPageChange, needShowPagination } = usePagination(PAGE_SIZE)
  const { companyId, gameId, generatePath } = useGenerateCompanyPath()
  const { isDeveloper } = useCurrentUser()

  const { data: webhooks = [], isLoading } = useQuery(webhooksQuery(companyId, gameId, query))

  const openCreateModal = useModal(props => (
    <CreateWebhookModal
      {...props}
      onClose={() => {
        navigate(generatePath(GAME_WEBHOOKS_PATH, { replace: true }))
      }}
    />
  ))

  useEffect(() => {
    if (props.isCreating) {
      setTimeout(() => {
        openCreateModal()
      }, 100) // TODO fix base modal
    }
  }, [props.isCreating])

  return (
    <Page>
      <PageHeader
        extra={
          isDeveloper &&
          !!webhooks.length && (
            <Button size="sm" onClick={() => openCreateModal()}>
              {t('webhook.add-webhook')}
            </Button>
          )
        }
      >
        {t('sidebar.webhooks')}
      </PageHeader>

      <WebhookDiscoverCallout />

      {!isLoading && page === 1 && !webhooks.length ? (
        <WebhookTableZeroState />
      ) : (
        <Table>
          <TableRow variant="header">
            <TableCell>{t('webhook.url')}</TableCell>
            <TableCell className="w-[300px]">{t('webhook.status-chart')}</TableCell>
            <TableCell className="flex max-w-36 items-center gap-1">
              {t('webhook.error-rate')}
              <TooltipAlertCircle message={t('webhook.error-rate.tooltip')} />
            </TableCell>
            <TableCell className="max-w-36">{t('webhook.status')}</TableCell>
          </TableRow>

          {!isLoading &&
            webhooks.map(webhook => (
              <WebhookRow canEdit={isDeveloper} key={`webhook-row-${webhook.id}`} webhook={webhook} />
            ))}
        </Table>
      )}

      {isLoading && (
        <Table>
          {new Array(PAGE_SIZE).fill('').map((_, index) => (
            <WebhookRowSkeleton key={`row-skeleton-${index}`} />
          ))}
        </Table>
      )}

      {needShowPagination(isLoading, webhooks) && (
        <Pagination hasNext={!isLoading && webhooks.length === PAGE_SIZE} page={page} onPageChange={onPageChange} />
      )}
    </Page>
  )
}
